/**
 * 챗봇 관련 타입 정의
 * - 채팅 데이터 타입
 * - API 요청/응답 타입
 * - 컴포넌트 props 타입
 */

import { Dispatch, SetStateAction } from 'react';
import { ChatbotFAQ } from 'src/@types/apiResponseTypes';

export type TSetState<T> = Dispatch<SetStateAction<T>>;

export const AI_RESPONSE_DEFAULT_VALUE: AIResponse = {
  user_id: 0,
  chat_id: '',
  chat_title: '',
  response: '',
  complete: false,
  ord: 0,
  reg_dt: '',
  is_faq: false,
};

export interface MessageInfo {
  id: string;
  // 메시지
  response: string;
  // 사용자인지 AI 봇인지를 구별
  isUser: boolean;
}

// MessageInfo 타입인지 아닌지 타입 체크
export function isMessageInfoType(obj: any): obj is MessageInfo {
  return (
    obj !== null &&
    typeof obj === 'object' &&
    typeof obj.chat_id === 'number' &&
    typeof obj.chat_title === 'string' &&
    typeof obj.response === 'string' &&
    typeof obj.isUser === 'boolean'
  );
}

export function isChatbotFAQType(object: any): object is ChatbotFAQ {
  return (
    typeof object === 'object' &&
    object !== null &&
    'faq_id' in object &&
    'question' in object &&
    'answer' in object &&
    'tag' in object
  );
}

export function isChatListType(object: any): object is ChatList {
  return (
    typeof object === 'object' &&
    object !== null &&
    'chat_id' in object &&
    'chat_title' in object &&
    'reg_dt' in object
  );
}

export function isChatListTypeArray(object: any): object is ChatList[] {
  return (
    Array.isArray(object) &&
    object.every(
      (item) =>
        typeof item === 'object' &&
        item !== null &&
        'chat_id' in item &&
        'chat_title' in item &&
        'reg_dt' in item,
    )
  );
}

export function isChatbotResponseType(object: any): object is ChatbotHistoryResponse {
  return (
    typeof object === 'object' &&
    object !== null &&
    typeof object.chat_id === 'string' &&
    Array.isArray(object.history) &&
    object.history.every(
      (item: any) =>
        typeof item === 'object' &&
        item !== null &&
        typeof item.ord === 'number' &&
        typeof item.user_id === 'number' &&
        typeof item.user_input === 'string' &&
        typeof item.response === 'string' &&
        typeof item.reg_dt === 'string',
    )
  );
}

/** AI 응답 데이터 타입 */
export interface AIResponse extends BaseResponseType {
  /** 채팅 타이틀 */
  chat_title: string;
  /** 스트림 데이터가 마지막인지 구별하는 플래그 */
  complete: boolean;
}

/** 채팅 목록 요청 파라미터 타입 */
export interface ChatListRequest {
  /** 사용자 ID */
  user_id: number;
  /** 페이지 번호 */
  page?: number;
  /** 페이지 크기 */
  size?: number;
}

/** 채팅방 기본 정보 타입 */
export interface ChatList {
  /** 채팅방 ID */
  chat_id: string;
  /** 채팅방 제목 */
  chat_title: string;
  /** 등록일시 */
  reg_dt: string;
}

/** 채팅 히스토리 타입 */
export interface ChatHistory {
  /** 메시지 순서 */
  ord: number;
  /** 사용자 ID */
  user_id: number;
  /** 사용자 입력 메시지 */
  user_input: string;
  /** AI 응답 메시지 */
  response: string;
  /** 등록일시 */
  reg_dt: string;
  /** FAQ 관련 질문 여부 */
  is_faq: boolean;
}

export interface ChatbotHistoryResponse {
  chat_id?: string;
  history: ChatHistory[];
}

export interface CurrentChatRoomIdState {
  currentChatRoomId: string;
  onSetCurrentRoomId: {
    onSetCurrentChatRoomId: (chat_id: string) => Promise<void>;
    onSetCurrentFAQRoomId: (faq_id: number) => Promise<void>;
  };
}

export interface BaseResponseType {
  // 유저 id
  user_id: number;
  // 단일 메시지의 id
  chat_id: string;
  // 챗봇 AI 메시지
  response: string;
  ord: number;
  reg_dt: string;
  is_faq: boolean;
}

export interface UserType {
  user_id: number;
  user_input: string;
  reg_dt: string;
}

export interface ConversationType {
  aiResponse: BaseResponseType;
  userMessage: UserType;
}

export function isConversationType(object: any): object is ConversationType {
  return (
    typeof object === 'object' &&
    object !== null &&
    typeof object.aiResponse === 'object' &&
    object.aiResponse !== null &&
    typeof object.aiResponse.chat_id === 'string' &&
    typeof object.aiResponse.ord === 'number' &&
    typeof object.aiResponse.reg_dt === 'string' &&
    typeof object.aiResponse.response === 'string' &&
    typeof object.aiResponse.user_id === 'number' &&
    typeof object.userMessage === 'object' &&
    object.userMessage !== null &&
    typeof object.userMessage.user_id === 'number' &&
    typeof object.userMessage.user_input === 'string'
  );
}

export function isConversationTypeArray(object: any): object is ConversationType[] {
  return Array.isArray(object) && object.every((item) => isConversationType(item));
}

export interface OnDeleteChatRoom {
  onDeleteSingleChatRoom: (id: string) => void;
  onDeleteAllChatRooms: () => void;
}
